import React, { useState } from "react";
import { Row, Col, Label, Input, Button, Table } from "reactstrap";
import { v4 as uuidv4 } from "uuid";
import { FaDownload } from "react-icons/fa";
// Round DMS-P2-2 — G1/G2: server attachments download through the
// authorized api endpoint (JWT), not an anonymous static URL.
import downloadDmsAttachment from "../../utils/downloadDmsAttachment";

const EcnAttachmentsTab = ({ data = [], setData = () => { } }) => {

    const [localFile, setLocalFile] = useState(null);
    const [title, setTitle] = useState("");
    const [subjectToApproval, setSubjectToApproval] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const toBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                try {
                    // reader.result = data:image/png;base64,XXXX
                    let base64 = reader.result.split(",")[1];

                    // remove spaces/newlines
                    base64 = base64.replace(/\s/g, "");

                    resolve(base64);
                } catch (err) {
                    reject(err);
                }
            };

            reader.onerror = error => reject(error);

            reader.readAsDataURL(file);
        });

    const handleAddAttachment = async () => {

        if (!localFile || !title) return;

        const base64 = await toBase64(localFile);

        const newAttachment = {
            id: uuidv4(),
            fileTitle: title,   // ← IMPORTANT
            fileName: localFile.name,
            rawFile: base64,
            subjectToApproval: subjectToApproval
        };

        setData([...data, newAttachment]);

        setLocalFile(null);
        setTitle("");
        setSubjectToApproval(false);
    };
    const handleDownload = (e, att) => {
        e.stopPropagation();

        if (att.downloadUrl) {
            // ✅ Already uploaded to server — Round DMS-P2-2: fetch through
            // the authorized api route with the JWT (legacy-blob rows
            // included); anonymous static /uploads is denied server-side.
            downloadDmsAttachment(att).catch((err) => {
                console.error("Attachment download failed", err);
                alert(
                    err?.response?.data?.error ||
                    "Attachment download failed. You may not have access to this document."
                );
            });
            return;
        }

        if (att.rawFile) {
            // ✅ Local base64 file — convert back and download
            try {
                const byteChars = atob(att.rawFile);
                const byteNumbers = Array.from(byteChars).map(c => c.charCodeAt(0));
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray]);
                const url = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = att.fileName || "attachment";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } catch (err) {
                console.error("Download error:", err);
            }
            return;
        }

        if (att.fileObject) {
            // ✅ Raw File object fallback
            const url = URL.createObjectURL(att.fileObject);
            const link = document.createElement("a");
            link.href = url;
            link.download = att.fileName || "attachment";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return;
        }

        alert("File not available for download.");
    };
    const removeSelected = () => {
        if (!selectedId) return;

        setData(data.filter(a => a.id !== selectedId));
        setSelectedId(null);
    };

    return (
        <>
            <h6>Attachments</h6>

            {/* Attachment Grid */}
            <div className="border p-3 mb-3">
                <Table bordered hover>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>File Name</th>
                            <th>Subject To Approval</th>
                            <th>Download</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && (
                            <tr>
                                <td colSpan="3" className="text-center">
                                    No attachments added.
                                </td>
                            </tr>
                        )}

                        {data.map(att => (
                            <tr
                                key={att.id}
                                onClick={() => setSelectedId(att.id)}
                                style={{
                                    backgroundColor: selectedId === att.id ? "#e7f1ff" : "",
                                    cursor: "pointer"
                                }}
                            >
                                <td>{att.fileTitle}</td>
                                <td>{att.fileName}</td>
                                <td>
                                    {/* Round R9 (2026) — Q18 — SubjectToApproval
                                        is now toggleable per row. Backend already
                                        accepts the flag via AttachmentDto.SubjectToApproval
                                        and writes to DmsAttachment.SubjectToApproval
                                        on submit. The toggle lifts state to the
                                        parent via setData so the value persists
                                        through the form's save path. */}
                                    <Input
                                        type="checkbox"
                                        checked={!!att.subjectToApproval}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const next = e.target.checked;
                                            setData(data.map(a =>
                                                a.id === att.id
                                                    ? { ...a, subjectToApproval: next }
                                                    : a
                                            ));
                                        }}
                                        aria-label={`Toggle Subject To Approval for ${att.fileName}`}
                                    />
                                </td>
                                <td className="text-center">
                                    {att.rawFile || att.downloadUrl || att.fileObject ? (
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={(e) => handleDownload(e, att)}
                                            title="Download file"
                                        >
                                            <i className="ri-download-2-line me-1" />
                                            Download
                                        </button>
                                    ) : (
                                        <span className="text-muted" style={{ fontSize: 12 }}>
                                            Not available
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            <div className="text-end">
                <Button
                    color="danger"
                    disabled={!selectedId}
                    onClick={removeSelected}
                >
                    Remove Selected Attachment
                </Button>
            </div>
            {/* Add Attachment Section */}
            <Row className="mb-3">
                <Col md={6}>
                    <Label>Local File</Label>
                    <Input
                        type="file"
                        onChange={(e) =>
                            setLocalFile(e.target.files[0])
                        }
                    />
                </Col>

                <Col md={6}>
                    <Label>Title</Label>
                    <Input
                        type="text"
                        value={title}
                        onChange={(e) =>
                            setTitle(e.target.value)
                        }
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={6}>
                    <Label>
                        Should this attachment be subject to the Approval Process?
                    </Label>

                    <div className="d-flex gap-3 mt-2">
                        <div className="form-check">
                            <Input
                                type="radio"
                                checked={subjectToApproval === true}
                                onChange={() =>
                                    setSubjectToApproval(true)
                                }
                            />
                            <Label>Yes</Label>
                        </div>

                        <div className="form-check">
                            <Input
                                type="radio"
                                checked={subjectToApproval === false}
                                onChange={() =>
                                    setSubjectToApproval(false)
                                }
                            />
                            <Label>No</Label>
                        </div>
                    </div>
                </Col>

                <Col md={6} className="d-flex align-items-end justify-content-end">
                    <Button
                        color="primary"
                        onClick={handleAddAttachment}
                    >
                        Add
                    </Button>
                </Col>
            </Row>


        </>
    );
};

export default EcnAttachmentsTab;